import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TextField,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  Button,
  useTheme,
  alpha,
  styled,
  Menu,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Card,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorOutlineIcon,
  FileDownload as FileDownloadIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import baseUrl from '../Config';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// =============================================
// STYLED COMPONENTS (matching CandidateList theme)
// =============================================
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha('#4BB648', 0.03),
  },
  '&:hover': {
    backgroundColor: alpha('#4BB648', 0.08),
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: alpha('#4BB648', 0.12),
  },
}));

const statusColors = {
  active: '#4caf50',
  inactive: '#f44336',
};

// =============================================
// HELPER FUNCTIONS
// =============================================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  return dateString ? dayjs(dateString).format('DD-MMM-YYYY') : '';
};

// =============================================
// EXPORT DIALOG
// =============================================
const ExportDialog = ({ open, onClose, onExport }) => {
  const [exportType, setExportType] = useState('all');

  const handleExport = () => {
    onExport(exportType);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: '#4BB648',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FileDownloadIcon />
        Export Price List
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <FormControl fullWidth sx={{ margin : '20px 0px' }}>
          <InputLabel>Export Type</InputLabel>
          <Select
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            label="Export Type"
          >
            <MenuItem value="all">All Entries</MenuItem>
            <MenuItem value="active">Active Only</MenuItem>
            <MenuItem value="inactive">Inactive Only</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary', mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          sx={{
            bgcolor: '#4BB648',
            '&:hover': { bgcolor: '#3D8B3A' },
          }}
          startIcon={<FileDownloadIcon />}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =============================================
// PRICE LIST FORM DIALOG (Add / Edit)
// =============================================
const PriceListFormDialog = ({ open, onClose, onSubmit, editData, filterOptions , masterProfessions }) => {
  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    license_authority: '',
    category: '',
    profession: '',
    service_name: '',
    price: '',
    remarks: '',
    last_updated: dayjs().format('YYYY-MM-DD'),
    updated_by: '',
    status: 'active',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      setFormData({
        license_authority: editData.license_authority || '',
        category: editData.category || '',
        profession: editData.profession || '',
        service_name: editData.service_name || '',
        price: editData.price || '',
        remarks: editData.remarks || '',
        last_updated: editData.last_updated
          ? dayjs(editData.last_updated).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        updated_by: editData.updated_by || '',
        status: editData.status || 'active',
      });
      setErrors({});
    } else {
      setFormData({
        license_authority: '',
        category: '',
        profession: '',
        service_name: '',
        price: '',
        remarks: '',
        last_updated: dayjs().format('YYYY-MM-DD'),
        updated_by: '',
        status: 'active',
      });
      setErrors({});
    }
  }, [editData, open]);

  const validate = () => {
    const newErrors = {};
    if (!formData.license_authority.trim())
      newErrors.license_authority = 'License Authority is required';
    if (!formData.service_name.trim())
      newErrors.service_name = 'Service Name is required';
    if (formData.price === '' || isNaN(parseFloat(formData.price)))
      newErrors.price = 'Valid price is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      ...formData,
      price: parseFloat(formData.price) || 0,
      last_updated: formData.last_updated || dayjs().format('YYYY-MM-DD'),
    });
  };

const formFields = useMemo(() => [
    {
      id: 'license_authority',
      label: 'License Authority *',
      type: 'text',
      xs: 12,
      sm: 6,
    },
    {
      id: 'category',
      label: 'Category',
      type: 'text',
      xs: 12,
      sm: 6,
    },
    {
    id: 'profession',
    label: 'Profession',
    type: 'select',
    xs: 12,
    sm: 6,
    options: masterProfessions.map(p => ({ value: p.profession_name, label: p.profession_name }))
  },
    {
      id: 'service_name',
      label: 'Service Name *',
      type: 'text',
      xs: 12,
      sm: 6,
    },
    {
      id: 'price',
      label: 'Price (INR) *',
      type: 'number',
      xs: 12,
      sm: 6,
    },
    {
      id: 'last_updated',
      label: 'Last Updated',
      type: 'date',
      xs: 12,
      sm: 6,
    },
    {
      id: 'updated_by',
      label: 'Updated By',
      type: 'text',
      xs: 12,
      sm: 6,
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      xs: 12,
      sm: 6,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
 ], [masterProfessions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 15px 40px rgba(0,0,0,0.2)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#4BB648',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
        }}
      >
        {isEdit ? <EditIcon /> : <AddIcon />}
        {isEdit ? 'Edit Price Entry' : 'Add New Price Entry'}
      </DialogTitle>
      <DialogContent sx={{ pt: 5, pb: 2 }}>
        {/* Replaces Grid container with flexbox */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 , margin : '20px'}}>
          {formFields.map((field) => (
            <Box
              key={field.id}
              sx={{
                flex: field.xs === 12 ? '1 1 35%' : { xs: '1 1 100%', sm: '1 1 calc(50% - 20px)' },
                minWidth: 0,
              }}
            >
              {field.type === 'select' ? (
                <FormControl fullWidth size="small" error={!!errors[field.id]}>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={formData[field.id]}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    label={field.label}
                  >
                    {field.options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  type={field.type}
                  value={formData[field.id]}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  error={!!errors[field.id]}
                  helperText={errors[field.id]}
                  InputProps={
                    field.id === 'price'
                      ? {
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }
                      : field.id === 'last_updated'
                      ? {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              )}
            </Box>
          ))}
        </Box>
        {/* Remarks field - full width */}
        <Box sx={{ mt: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            label="Remarks"
            multiline
            rows={3}
            value={formData.remarks}
            onChange={(e) => handleChange('remarks', e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: 'text.secondary',
            borderColor: 'text.secondary',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
          }}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            bgcolor: '#4BB648',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: '#3D8B3A' },
          }}
          startIcon={isEdit ? <CheckCircleIcon /> : <AddIcon />}
        >
          {isEdit ? 'Update' : 'Add Entry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =============================================
// VIEW DETAIL DIALOG
// =============================================
const ViewDetailDialog = ({ open, onClose, entry }) => {
  if (!entry) return null;

  const detailFields = [
    { label: 'License Authority', value: entry.license_authority },
    { label: 'Category', value: entry.category },
    { label: 'Profession', value: entry.profession },
    { label: 'Service Name', value: entry.service_name },
    { label: 'Price', value: formatCurrency(entry.price), highlight: true },
    { label: 'Remarks', value: entry.remarks },
    { label: 'Last Updated', value: formatDate(entry.last_updated) },
    { label: 'Updated By', value: entry.updated_by },
    { label: 'Status', value: entry.status },
    { label: 'Created At', value: formatDate(entry.created_at) },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 15px 40px rgba(0,0,0,0.2)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#4BB648',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
        }}
      >
        <VisibilityIcon />
        Price Entry Details
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Price highlight card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: alpha('#4BB648', 0.08),
            borderRadius: 2,
            textAlign: 'center',
            border: `1px solid ${alpha('#4BB648', 0.2)}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Service Price
          </Typography>
          <Typography variant="h4" fontWeight="700" sx={{ color: '#4BB648' }}>
            {formatCurrency(entry.price)}
          </Typography>
          <Typography variant="body1" fontWeight="500" mt={1}>
            {entry.service_name}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Chip
              label={entry.license_authority}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#4BB648', color: '#4BB648' }}
            />
            <Chip
              label={entry.category || 'N/A'}
              size="small"
              variant="outlined"
            />
            <Chip
              label={entry.profession || 'N/A'}
              size="small"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Detail fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {detailFields
            .filter((f) => !f.highlight)
            .map((field, index) => (
              <Box
                key={field.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.5,
                  px: 2,
                  bgcolor: index % 2 === 0 ? alpha('#4BB648', 0.02) : 'transparent',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {field.label}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {field.value || 'N/A'}
                  {field.label === 'Status' && (
                    <Chip
                      label={field.value}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor:
                          statusColors[field.value?.toLowerCase()] || '#9e9e9e',
                        color: '#fff',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Typography>
              </Box>
            ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#4BB648',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: '#3D8B3A' },
          }}
          startIcon={<CheckCircleIcon />}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =============================================
// UPLOAD EXCEL DIALOG
// =============================================
const UploadExcelDialog = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
      ];
      if (!allowedTypes.includes(selected.type) && !selected.name.match(/\.(xlsx|xls|csv)$/i)) {
        setError('Please upload a valid Excel or CSV file');
        return;
      }
      setFile(selected);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('excelFile', file);

      const response = await axios.post(`${baseUrl}/price-list/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(response.data);
      if (response.data.insertedCount > 0) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#4BB648',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <UploadIcon />
        Import from Excel
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {/* Expected format info */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#f5f5f5',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            Expected Column Headers:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {[
              'License Authority',
              'Category',
              'Profession',
              'Service Name',
              'Price',
              'Remarks',
              'Last Updated',
              'Updated By',
            ].map((col) => (
              <Chip key={col} label={col} size="small" variant="outlined" />
            ))}
          </Box>
        </Paper>

        {/* File input */}
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: file ? alpha('#4BB648', 0.05) : '#fafafa',
            borderColor: file ? '#4BB648' : '#ccc',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#4BB648',
              bgcolor: alpha('#4BB648', 0.03),
            },
          }}
          onClick={() => document.getElementById('excel-upload-input').click()}
        >
          <input
            id="excel-upload-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {file ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4BB648', mb: 1 }} />
              <Typography variant="body1" fontWeight="500">
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
            </>
          ) : (
            <>
              <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Click to select Excel/CSV file
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports .xlsx, .xls, .csv
              </Typography>
            </>
          )}
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Result */}
        {result && (
          <Alert severity={result.errors ? 'warning' : 'success'} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {result.message}
            </Typography>
            {result.errors && result.errors.length > 0 && (
              <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                {result.errors.slice(0, 5).map((err, idx) => (
                  <Typography key={idx} variant="caption" display="block" color="error">
                    Row {err.row}: {err.reason}
                  </Typography>
                ))}
                {result.errors.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {result.errors.length - 5} more errors
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: 'text.secondary',
            borderColor: 'text.secondary',
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!file || uploading}
          sx={{
            bgcolor: '#4BB648',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: '#3D8B3A' },
            '&.Mui-disabled': { bgcolor: '#c8e6c9' },
          }}
          startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =============================================
// ADD PROFESSION DIALOG
// =============================================
const AddProfessionDialog = ({ open, onClose, onSuccess }) => {
  const [professionName, setProfessionName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!professionName.trim()) {
      setError('Profession name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${baseUrl}/master-professions`, {
        profession_name: professionName.trim(),
        description: description.trim() || null,
        status: 'active'
      });

      if (response.data.success || response.data.id) {
        onSuccess(professionName.trim());
        setProfessionName('');
        setDescription('');
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add profession');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#4BB648',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2
        }}
      >
        <CategoryIcon />
        Add New Profession
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Profession Name *"
          value={professionName}
          onChange={(e) => setProfessionName(e.target.value)}
          margin="normal"
          autoFocus
          error={!!error && !professionName}
          helperText={!professionName && error ? error : ''}
          sx={{ mb: 2 }}
        />
        
        {/* <TextField
          fullWidth
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={3}
        /> */}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: 'text.secondary',
            borderColor: 'text.secondary',
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: '#4BB648',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: '#3D8B3A' }
          }}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading ? 'Adding...' : 'Add Profession'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =============================================
// MAIN MASTER PRICE LIST COMPONENT
// =============================================
const MasterPriceList = () => {
  const theme = useTheme();

  // Data state
  const [priceList, setPriceList] = useState([]);
  // Add this state alongside other state declarations
const [masterProfessions, setMasterProfessions] = useState([]);
const [addProfessionDialogOpen, setAddProfessionDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAuthority, setSelectedAuthority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProfession, setSelectedProfession] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewEntry, setViewEntry] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Filter options (distinct values)
  const [filterOptions, setFilterOptions] = useState({
    license_authorities: [],
    categories: [],
    professions: [],
  });

  // Stats
  const [stats, setStats] = useState(null);


  const fetchMasterProfessions = async () => {
  try {
    const response = await axios.get(`${baseUrl}/master-professions`);
    setMasterProfessions(response.data); // array of { id, profession_name, ... }
  } catch (err) {
    console.error('Error fetching master professions:', err);
  }
};

useEffect(() => {
  fetchFilterOptions();
  fetchStats();
  fetchMasterProfessions();      // <-- add this
}, []);

  // =============================================
  // API CALLS
  // =============================================
  const fetchPriceList = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedAuthority !== 'all') params.license_authority = selectedAuthority;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedProfession !== 'all') params.profession = selectedProfession;
      if (selectedStatus !== 'all') params.status = selectedStatus;
      if (searchTerm) params.search = searchTerm;
      params.sort_by = orderBy;
      params.sort_order = order;

      const response = await axios.get(`${baseUrl}/price-list`, { params });
      setPriceList(response.data);
    } catch (err) {
      setError('Failed to fetch price list. Please try again.');
      console.error('Error fetching price list:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${baseUrl}/price-list/filters/distinct`);
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };


  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${baseUrl}/price-list/stats/summary`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const createEntry = async (data) => {
    try {
      await axios.post(`${baseUrl}/price-list`, data);
      showNotification('Price entry added successfully', 'success');
      setFormDialogOpen(false);
      fetchPriceList();
      fetchFilterOptions();
      fetchStats();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to add entry', 'error');
    }
  };

  const updateEntry = async (id, data) => {
    try {
      await axios.put(`${baseUrl}/price-list/${id}`, data);
      showNotification('Price entry updated successfully', 'success');
      setFormDialogOpen(false);
      setEditData(null);
      fetchPriceList();
      fetchFilterOptions();
      fetchStats();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to update entry', 'error');
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${baseUrl}/price-list/${id}`);
      showNotification('Price entry deleted successfully', 'success');
      setDeleteDialogOpen(false);
      fetchPriceList();
      fetchFilterOptions();
      fetchStats();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to delete entry', 'error');
    }
  };

  // =============================================
  // EVENT HANDLERS
  // =============================================
  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleFormSubmit = (data) => {
    if (editData) {
      updateEntry(editData.id, data);
    } else {
      createEntry(data);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, entry) => {
    setAnchorEl(event.currentTarget);
    setSelectedEntry(entry);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEntry(null);
  };

  const handleEdit = () => {
    if (selectedEntry) {
      setEditData(selectedEntry);
      setFormDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedEntry) {
      setViewEntry(selectedEntry);
      setViewDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (selectedEntry) {
      setSelectedEntry(selectedEntry);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (selectedEntry) {
      deleteEntry(selectedEntry.id);
    }
  };

  // =============================================
  // FILTERING & SORTING
  // =============================================
  useEffect(() => {
    fetchPriceList();
  }, [selectedAuthority, selectedCategory, selectedProfession, selectedStatus, searchTerm, orderBy, order]);

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
  }, []);

  // Client-side filtering for search (server also handles it, but this provides instant UX)
  const filteredAndSorted = useMemo(() => {
    return [...priceList];
  }, [priceList]);

  const paginatedData = useMemo(() => {
    return filteredAndSorted.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredAndSorted, page, rowsPerPage]);

  // =============================================
  // EXPORT
  // =============================================
  const handleExport = (exportType) => {
    let dataToExport = priceList;

    if (exportType === 'active') {
      dataToExport = priceList.filter((item) => item.status === 'active');
    } else if (exportType === 'inactive') {
      dataToExport = priceList.filter((item) => item.status === 'inactive');
    }

    const formattedData = dataToExport.map((item, index) => ({
      'S.No': index + 1,
      'License Authority': item.license_authority,
      'Category': item.category,
      'Profession': item.profession,
      'Service Name': item.service_name,
      'Price (INR)': item.price,
      'Remarks': item.remarks || '',
      'Last Updated': formatDate(item.last_updated),
      'Updated By': item.updated_by || '',
      'Status': item.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 40 },
      { wch: 15 },
      { wch: 40 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Price List');
    XLSX.writeFile(workbook, `MasterPriceList_${dayjs().format('DD-MMM-YYYY')}.xlsx`);
  };

  // =============================================
  // TABLE COLUMNS
  // =============================================
  const columns = [
    { id: 'license_authority', label: 'License Authority', width: '15%' },
    { id: 'category', label: 'Category', width: '11%' },
    //profession column is hidden in table but used in filters and export
    { id: 'profession', label: 'Profession', width: '18%' },
    { id: 'service_name', label: 'Service Name', width: '30%' },
    { id: 'price', label: 'Price', width: '9%' },
    { id: 'remarks', label: 'Remarks', width: '15%' },
    { id: 'updated_by', label: 'Updated By', width: '9%' },
    { id: 'actions', label: 'Actions', width: '5%' },
  ];

  // =============================================
  // RENDER
  // =============================================
  if (loading && priceList.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && priceList.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={3}>
        <ErrorOutlineIcon color="error" style={{ fontSize: 60 }} />
        <Typography variant="h6" color="error" mt={2}>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchPriceList}
          sx={{ mt: 3 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          marginTop: '79px',
          width: '100%',
        }}
      >
        {/* ============================================= */}
        {/* HEADER BAR */}
        {/* ============================================= */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {/* Title row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" component="h1" fontWeight="600">
                Master Price List
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.8, mt: 0.5, color: theme.palette.text.secondary }}
              >
                {filteredAndSorted.length} service(s) found
              </Typography>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditData(null);
                  setFormDialogOpen(true);
                }}
                sx={{
                  bgcolor: '#4BB648',
                  color: '#fff',
                  '&:hover': { bgcolor: '#3A9139' },
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Add Entry
              </Button>

              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  borderColor: '#4BB648',
                  color: '#4BB648',
                  '&:hover': {
                    borderColor: '#3A9139',
                    bgcolor: alpha('#4BB648', 0.05),
                  },
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Import Excel
              </Button>

              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
                sx={{
                  bgcolor: '#4BB648',
                  color: '#fff',
                  '&:hover': { bgcolor: '#3A9139' },
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Export
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchPriceList();
                  fetchFilterOptions();
                  fetchStats();
                }}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>


          {/* Filters row */}
<Box
  sx={{
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 1.5,
  }}
>
  <FormControl size="small" sx={{ minWidth: 160 }}>
    <InputLabel>License Authority</InputLabel>
    <Select
      value={selectedAuthority}
      onChange={(e) => {
        setSelectedAuthority(e.target.value);
        setPage(0);
      }}
      label="License Authority"
    >
      <MenuItem value="all">All Authorities</MenuItem>
      {filterOptions.license_authorities.map((auth) => (
        <MenuItem key={auth} value={auth}>
          {auth}
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ minWidth: 140 }}>
    <InputLabel>Category</InputLabel>
    <Select
      value={selectedCategory}
      onChange={(e) => {
        setSelectedCategory(e.target.value);
        setPage(0);
      }}
      label="Category"
    >
      <MenuItem value="all">All Categories</MenuItem>
      {filterOptions.categories.map((cat) => (
        <MenuItem key={cat} value={cat}>
          {cat}
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  {/* ADD PROFESSION FILTER HERE */}


<FormControl size="small" sx={{ minWidth: 200 }}>
  <InputLabel>Profession</InputLabel>
  <Select
    value={selectedProfession}
    onChange={(e) => {
      if (e.target.value === 'add_new') return;
      setSelectedProfession(e.target.value);
      setPage(0);
    }}
    label="Profession"
  >
    <MenuItem value="all">All Professions</MenuItem>
    {masterProfessions.map((prof) => (
      <MenuItem key={prof.id} value={prof.profession_name}>
        {prof.profession_name}
      </MenuItem>
    ))}
    <Divider />
    <MenuItem
      value="add_new"
      onClick={() => setAddProfessionDialogOpen(true)}
      sx={{ color: '#4BB648' }}
    >
      <AddIcon fontSize="small" sx={{ mr: 1 }} />
      Add New Profession
    </MenuItem>
  </Select>
</FormControl>


  <FormControl size="small" sx={{ minWidth: 120 }}>
    <InputLabel>Status</InputLabel>
    <Select
      value={selectedStatus}
      onChange={(e) => {
        setSelectedStatus(e.target.value);
        setPage(0);
      }}
      label="Status"
    >
      <MenuItem value="all">All Status</MenuItem>
      <MenuItem value="active">Active</MenuItem>
      <MenuItem value="inactive">Inactive</MenuItem>
    </Select>
  </FormControl>

  <TextField
    variant="outlined"
    placeholder="Search..."
    size="small"
    value={searchTerm}
    onChange={(e) => {
      setSearchTerm(e.target.value);
      setPage(0);
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon color="action" />
        </InputAdornment>
      ),
      sx: {
        bgcolor: theme.palette.background.default,
        borderRadius: 1,
      },
    }}
    sx={{
      minWidth: 200,
      '& .MuiOutlinedInput-root': {
        '&:hover fieldset': {
          borderColor: theme.palette.primary.main,
        },
      },
    }}
  />
</Box>
        </Box>

        {/* ============================================= */}
        {/* SUMMARY STATS CARDS - Grid replaced with flexbox */}
        {/* ============================================= */}
        {stats && (
          <Box sx={{ p: 2, pb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                '& > *': {
                  flex: { xs: '1 1 calc(50% - 12px)', sm: '1 1 calc(25% - 12px)' },
                  minWidth: { xs: 'calc(50% - 12px)', sm: '160px' },
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha('#4BB648', 0.06),
                  border: `1px solid ${alpha('#4BB648', 0.15)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <InventoryIcon sx={{ color: '#4BB648', fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Services
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#4BB648' }}>
                    {stats.totalEntries}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha('#2196f3', 0.06),
                  border: `1px solid ${alpha('#2196f3', 0.15)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <BusinessIcon sx={{ color: '#2196f3', fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Authorities
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#2196f3' }}>
                    {stats.authorityCount}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha('#ff9800', 0.06),
                  border: `1px solid ${alpha('#ff9800', 0.15)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <CategoryIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Categories
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#ff9800' }}>
                    {stats.categoryCount}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}

        {/* ============================================= */}
        {/* TABLE */}
        {/* ============================================= */}
        <Box sx={{ p: 0.5 }}>
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 350px)',
              overflow: 'auto',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sortDirection={orderBy === col.id ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f5f5f5',
                        width: col.width,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col.id !== 'actions' ? (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleRequestSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((entry) => (
                    <StyledTableRow key={entry.id} hover>
                      <TableCell>
                        <Chip
                          label={entry.license_authority}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#4BB648',
                            color: '#4BB648',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.category || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.profession || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500" sx={{ maxWidth: 250 }}>
                          {entry.service_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {formatCurrency(entry.price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={entry.remarks || ''} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary',
                            }}
                          >
                            {entry.remarks || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.updated_by || '—'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Actions">
                          <IconButton
                            onClick={(e) => handleMenuClick(e, entry)}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: alpha('#4BB648', 0.1),
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <SearchIcon fontSize="large" color="disabled" />
                        <Typography variant="h6" color="textSecondary" mt={1}>
                          No price list entries found
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mt={0.5}>
                          Try adjusting your filters or add a new entry
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ============================================= */}
          {/* CONTEXT MENU */}
          {/* ============================================= */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 3,
              sx: {
                minWidth: 180,
                borderRadius: 2,
                py: 0.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  typography: 'body2',
                },
              },
            }}
          >
            <MenuItem onClick={handleView}>
              <VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
              View Details
            </MenuItem>
            <MenuItem onClick={handleEdit}>
              <EditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} />
              <Typography color="error">Delete</Typography>
            </MenuItem>
          </Menu>

          {/* ============================================= */}
          {/* PAGINATION */}
          {/* ============================================= */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredAndSorted.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 2,
              '& .MuiTablePagination-toolbar': { px: 0 },
            }}
          />
        </Box>
      </Paper>

      {/* ============================================= */}
      {/* DIALOGS */}
      {/* ============================================= */}

      {/* Form Dialog (Add/Edit) */}
      <PriceListFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditData(null);
        }}
        onSubmit={handleFormSubmit}
        editData={editData}
        filterOptions={filterOptions}
          masterProfessions={masterProfessions} 
      />

      {/* View Detail Dialog */}
      <ViewDetailDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewEntry(null);
        }}
        entry={viewEntry}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: '#f44336',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            Are you sure you want to delete the price entry for{' '}
            <strong>{selectedEntry?.service_name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'text.secondary', mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ '&:hover': { bgcolor: theme.palette.error.dark } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExport}
      />

      {/* Upload Excel Dialog */}
      <UploadExcelDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => {
          fetchPriceList();
          fetchFilterOptions();
          fetchStats();
        }}
      />

      <AddProfessionDialog
  open={addProfessionDialogOpen}
  onClose={() => setAddProfessionDialogOpen(false)}
  onSuccess={(newProfessionName) => {
    fetchMasterProfessions();
    setSelectedProfession(newProfessionName); // auto‑select new profession
    setPage(0);
  }}
/>


      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MasterPriceList;



